import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { themeAPI } from '../services/api';

// PREDEFINED THEMES - as OBJECT (ThemeCreatorModal expects this format)
const PREDEFINED_THEMES = {
  default: {
    id: 'default',
    name: 'default',
    displayName: 'Default',
    description: 'Classic red and nude',
    type: 'default',
    colors: {
      primary: '#e60023',
      secondary: '#E8DED3',
      accent: '#0076d3',
      background: '#F2E8DD',
      cardBackground: '#E8DED3',
      text: '#111111',
      textSecondary: '#767676',
      border: '#DFD1C5',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  spring: {
    id: 'spring',
    name: 'spring',
    displayName: 'Spring',
    description: 'Fresh and vibrant',
    type: 'seasonal',
    colors: {
      primary: '#ec4899',
      secondary: '#fce7f3',
      accent: '#f472b6',
      background: '#fdf2f8',
      cardBackground: '#f5d9e8',
      text: '#831843',
      textSecondary: '#9d174d',
      border: '#fbcfe8',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  summer: {
    id: 'summer',
    name: 'summer',
    displayName: 'Summer',
    description: 'Bright and sunny',
    type: 'seasonal',
    colors: {
      primary: '#0ea5e9',
      secondary: '#e0f2fe',
      accent: '#38bdf8',
      background: '#f0f9ff',
      cardBackground: '#d6ecf7',
      text: '#0c4a6e',
      textSecondary: '#0369a1',
      border: '#bae6fd',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  autumn: {
    id: 'autumn',
    name: 'autumn',
    displayName: 'Autumn',
    description: 'Warm and cozy',
    type: 'seasonal',
    colors: {
      primary: '#d97706',
      secondary: '#fef3c7',
      accent: '#f59e0b',
      background: '#fffbeb',
      cardBackground: '#fef3c7',
      text: '#78350f',
      textSecondary: '#92400e',
      border: '#fde68a',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  winter: {
    id: 'winter',
    name: 'winter',
    displayName: 'Winter',
    description: 'Cool and crisp',
    type: 'seasonal',
    colors: {
      primary: '#6366f1',
      secondary: '#e0e7ff',
      accent: '#818cf8',
      background: '#eef2ff',
      cardBackground: '#dde4ff',
      text: '#3730a3',
      textSecondary: '#4f46e5',
      border: '#c7d2fe',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    }
  },
  christmas: {
    id: 'christmas',
    name: 'christmas',
    displayName: 'Christmas Joy',
    description: 'Festive red and green',
    type: 'holiday',
    colors: {
      primary: '#dc2626',
      secondary: '#fef2f2',
      accent: '#16a34a',
      background: '#fafafa',
      cardBackground: '#e5e5e5',
      text: '#1f2937',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      success: '#16a34a',
      warning: '#f59e0b',
      error: '#dc2626'
    }
  },
  halloween: {
    id: 'halloween',
    name: 'halloween',
    displayName: 'Spooky Halloween',
    description: 'Orange and black',
    type: 'holiday',
    colors: {
      primary: '#f97316',
      secondary: '#1f2937',
      accent: '#a855f7',
      background: '#111827',
      cardBackground: '#1f2937',
      text: '#f9fafb',
      textSecondary: '#9ca3af',
      border: '#374151',
      success: '#10b981',
      warning: '#f97316',
      error: '#ef4444'
    }
  }
};

const DEFAULT_COLORS = PREDEFINED_THEMES.default.colors;

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback if used outside provider
    return {
      themeColors: DEFAULT_COLORS,
      currentTheme: null,
      availableThemes: Object.values(PREDEFINED_THEMES),
      predefinedThemes: PREDEFINED_THEMES,
      loading: false,
      activateTheme: () => Promise.resolve(false),
      createTheme: () => Promise.resolve(null),
      loadThemes: () => {},
      setUserActive: () => {},
      setModalOpenState: () => {}
    };
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeColors, setThemeColors] = useState(DEFAULT_COLORS);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [availableThemes, setAvailableThemes] = useState(Object.values(PREDEFINED_THEMES));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserActive, setIsUserActive] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // *** CRITICAL: Apply CSS variables to document root ***
  const applyColorsToDocument = useCallback((colors) => {
    const root = document.documentElement;

    // Apply each color as a CSS variable
    Object.entries(colors).forEach(([key, value]) => {
      // Convert camelCase to kebab-case: cardBackground -> card-background
      const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // Also set some common aliases
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--background', colors.background);
    root.style.setProperty('--cardBackground', colors.cardBackground);
    root.style.setProperty('--card-background', colors.cardBackground);
    root.style.setProperty('--text', colors.text);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--textSecondary', colors.textSecondary);
    root.style.setProperty('--border', colors.border || '#DFD1C5');
    root.style.setProperty('--success', colors.success || '#10b981');
    root.style.setProperty('--warning', colors.warning || '#f59e0b');
    root.style.setProperty('--error', colors.error || '#ef4444');

    // Set body background and text color directly
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.text;

    console.log('✅ Theme colors applied to document:', colors);
  }, []);

  // Apply colors whenever themeColors changes
  useEffect(() => {
    applyColorsToDocument(themeColors);
  }, [themeColors, applyColorsToDocument]);

  // Load themes on mount
  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get current active theme from backend
      let activeThemeData = null;

      try {
        const currentResponse = await themeAPI.getCurrentTheme();
        if (currentResponse?.data?.success && currentResponse.data.data) {
          activeThemeData = currentResponse.data.data;
        }
      } catch (e) {
        console.warn('getCurrentTheme failed, trying getActiveTheme:', e.message);
        try {
          const activeResponse = await themeAPI.getActiveTheme();
          if (activeResponse?.data?.success && activeResponse.data.data) {
            activeThemeData = activeResponse.data.data;
          }
        } catch (e2) {
          console.warn('getActiveTheme also failed, using default theme');
        }
      }

      // If we got an active theme, apply it
      if (activeThemeData && activeThemeData.colors) {
        const colors = { ...DEFAULT_COLORS, ...activeThemeData.colors };
        setCurrentTheme(activeThemeData);
        setThemeColors(colors);
        console.log('✅ Loaded active theme:', activeThemeData.displayName || activeThemeData.name);
      } else {
        // Use default theme
        setCurrentTheme(PREDEFINED_THEMES.default);
        setThemeColors(DEFAULT_COLORS);
        console.log('ℹ️ Using default theme');
      }

      // Load all available themes
      try {
        const allResponse = await themeAPI.getThemes();
        if (allResponse?.data?.success && allResponse.data.data) {
          const dbThemes = allResponse.data.data;

          // Merge database themes with predefined themes
          const merged = { ...PREDEFINED_THEMES };

          dbThemes.forEach(dbTheme => {
            const themeName = dbTheme.name?.toLowerCase();
            if (themeName && merged[themeName]) {
              // Update predefined with DB version (keeps isActive status)
              merged[themeName] = {
                ...merged[themeName],
                ...dbTheme,
                id: dbTheme._id || dbTheme.id || themeName
              };
            } else if (dbTheme._id || dbTheme.id) {
              // Add custom theme
              merged[dbTheme._id || dbTheme.id] = {
                ...dbTheme,
                id: dbTheme._id || dbTheme.id
              };
            }
          });

          setAvailableThemes(Object.values(merged));
        }
      } catch (e) {
        console.warn('Could not load themes list:', e.message);
        setAvailableThemes(Object.values(PREDEFINED_THEMES));
      }

    } catch (error) {
      console.error('Failed to load themes:', error);
      setError(error.message);
      setThemeColors(DEFAULT_COLORS);
    } finally {
      setLoading(false);
    }
  };

  // *** CRITICAL: activateTheme must apply colors after success ***
  const activateTheme = async (themeIdOrName) => {
    try {
      setLoading(true);
      console.log('🎨 Activating theme:', themeIdOrName);

      // Check if it's a predefined theme by name
      const normalizedName = String(themeIdOrName).toLowerCase();
      const predefined = PREDEFINED_THEMES[normalizedName];

      let response;
      let activatedTheme;

      if (predefined) {
        // Use activate-by-name for predefined themes
        console.log('Using activate-by-name for predefined theme:', normalizedName);
        try {
          response = await themeAPI.activateThemeByName(normalizedName);
        } catch (e) {
          // Fallback: just apply locally if API fails
          console.warn('API call failed, applying theme locally');
          activatedTheme = predefined;
        }
      } else {
        // Use activate by ID for database themes
        console.log('Using activate by ID:', themeIdOrName);
        response = await themeAPI.activateTheme(themeIdOrName);
      }

      // Get activated theme from response or use predefined
      if (response?.data?.success && response.data.data) {
        activatedTheme = response.data.data;
      } else if (!activatedTheme && predefined) {
        activatedTheme = predefined;
      }

      if (activatedTheme) {
        // *** THIS IS THE KEY: Apply the colors immediately ***
        const newColors = { ...DEFAULT_COLORS, ...activatedTheme.colors };
        setCurrentTheme(activatedTheme);
        setThemeColors(newColors);

        console.log('✅ Theme activated and colors applied:', activatedTheme.displayName || activatedTheme.name);

        // Refresh themes list to update "active" badges
        await loadThemes();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to activate theme:', error);
      setError(error.message);

      // Even if API fails, try to apply predefined theme locally
      const normalizedName = String(themeIdOrName).toLowerCase();
      const predefined = PREDEFINED_THEMES[normalizedName];
      if (predefined) {
        console.log('Applying predefined theme locally as fallback');
        setCurrentTheme(predefined);
        setThemeColors({ ...DEFAULT_COLORS, ...predefined.colors });
        return true;
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  // Create new theme
  const createTheme = async (themeData) => {
    try {
      setLoading(true);
      console.log('Creating theme:', themeData);

      const response = await themeAPI.createTheme(themeData);

      if (response?.data?.success) {
        console.log('✅ Theme created:', response.data.data);
        await loadThemes(); // Refresh list
        return response.data.data;
      }

      throw new Error('Failed to create theme');
    } catch (error) {
      console.error('Failed to create theme:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update existing theme
  const updateTheme = async (themeId, themeData) => {
    try {
      setLoading(true);
      const response = await themeAPI.updateTheme(themeId, themeData);
      if (response?.data?.success) {
        await loadThemes();
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete theme
  const deleteTheme = async (themeId) => {
    try {
      setLoading(true);
      const response = await themeAPI.deleteTheme(themeId);
      if (response?.data?.success) {
        await loadThemes();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to delete theme:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Preview theme temporarily (without saving)
  const previewTheme = (theme) => {
    if (theme?.colors) {
      const previewColors = { ...DEFAULT_COLORS, ...theme.colors };
      applyColorsToDocument(previewColors);
    }
  };

  // Cancel preview and restore current theme
  const cancelPreview = () => {
    if (currentTheme?.colors) {
      applyColorsToDocument({ ...DEFAULT_COLORS, ...currentTheme.colors });
    } else {
      applyColorsToDocument(DEFAULT_COLORS);
    }
  };

  const value = {
    // State
    themeColors,
    currentTheme,
    availableThemes,
    predefinedThemes: PREDEFINED_THEMES, // Object format for ThemeCreatorModal
    loading,
    error,

    // Actions
    loadThemes,
    activateTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    previewTheme,
    cancelPreview,

    // UI State helpers
    setUserActive: setIsUserActive,
    setModalOpenState: setIsModalOpen,
    isUserActive,
    isModalOpen
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export { ThemeContext };
export default ThemeContext;
